import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
    CheckCircle2,
    ArrowRight,
    Undo2,
    RefreshCw,
    BookCheck,
    AlertTriangle,
    Info,
} from 'lucide-react';
import * as YearEndService from '../../../../services/YearEndService';
import { formatSek } from '../constants';

/**
 * Step 4 for an ENSKILD FIRMA — "Årets resultat".
 *
 * The AB version of this step computes bolagsskatt and books
 * bokslutsdispositioner. Neither applies here: an enskild näringsidkare is
 * not its own skattesubjekt (the result is taxed with the owner via INK1 +
 * NE-bilagan), and periodiseringsfond/expansionsfond/räntefördelning are
 * deductions in the DEKLARATION, not entries in the bookkeeping. So this
 * step books exactly one thing — årets resultat over to eget kapital
 * (8999 → 2019) — and then locks the year.
 */
const EfResultStep = ({
    companyId,
    processId,
    completed,
    onComplete,
    onUndo,
    busy,
    gateError,
}) => {
    const [data, setData] = useState(null);
    const [loadError, setLoadError] = useState(null);
    const [refreshing, setRefreshing] = useState(false);
    const [booking, setBooking] = useState(false);
    const [bookError, setBookError] = useState(null);
    const [confirmUndo, setConfirmUndo] = useState(false);
    const [undoing, setUndoing] = useState(false);

    const fetchPreview = useCallback(async ({ silent = false } = {}) => {
        if (!processId) return;
        if (!silent) setRefreshing(true);
        setLoadError(null);
        try {
            const resp = await YearEndService.getEfResult(companyId, processId);
            setData(resp?.data || null);
        } catch (err) {
            setLoadError(err.response?.data?.message || 'Kunde inte hämta årets resultat.');
        } finally {
            setRefreshing(false);
        }
    }, [companyId, processId]);

    useEffect(() => { fetchPreview({ silent: true }); }, [fetchPreview]);

    const booked = Boolean(data?.booked);
    const resultat = data?.aretsResultat ?? 0;
    const vinst = resultat >= 0;

    const handleBook = async () => {
        setBooking(true);
        setBookError(null);
        try {
            await YearEndService.bookEfResult(companyId, processId);
            toast.success('Årets resultat är bokfört.');
            await fetchPreview({ silent: true });
        } catch (err) {
            setBookError(err.response?.data?.message || 'Kunde inte bokföra årets resultat.');
        } finally {
            setBooking(false);
        }
    };

    const handleUndoBooking = async () => {
        setUndoing(true);
        try {
            await YearEndService.undoEfResult(companyId, processId);
            toast.success('Bokföringen är ångrad — rättelseverifikat har skapats.');
            setConfirmUndo(false);
            await fetchPreview({ silent: true });
        } catch (err) {
            toast.error(err.response?.data?.message || 'Kunde inte ångra bokföringen.');
        } finally {
            setUndoing(false);
        }
    };

    return (
        <>
            <div className="modern-card-header ye-step-header">
                <div>
                    <h4 className="section-title">
                        Årets resultat
                        {data?.fiscalYear?.year ? ` — räkenskapsåret ${data.fiscalYear.year}` : ''}
                    </h4>
                    <p className="section-subtitle">
                        En enskild firma betalar ingen egen bolagsskatt — resultatet beskattas hos
                        dig via inkomstdeklarationen (INK1 + NE-bilagan). Här bokförs bara årets
                        resultat över till eget kapital, sedan låses räkenskapsåret.
                    </p>
                </div>
                <div className="action-buttons">
                    <button
                        className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center gap-2"
                        onClick={() => fetchPreview()}
                        disabled={refreshing}
                    >
                        <RefreshCw size={14} className={refreshing ? 'ye-spin' : undefined} />
                        Uppdatera
                    </button>
                </div>
            </div>

            <div className="modern-card-body">
            {data?.explanation && (
                <div className="ye-well d-flex gap-2" style={{ marginBottom: '1rem' }}>
                    <Info size={16} style={{ flexShrink: 0, marginTop: 2 }} />
                    <span>{data.explanation}</span>
                </div>
            )}

            {loadError && (
                <div className="alert alert-warning d-flex align-items-center gap-2">
                    <AlertTriangle size={18} />
                    <span>{loadError}</span>
                </div>
            )}

            {data && (
                <div className="ye-ps-section">
                    <h5 className="ye-section-heading">Så här bokförs det</h5>
                    <div className="ye-tax-rows">
                        <div className="ye-tax-row ye-tax-row-group">
                            <span>{vinst ? 'Årets vinst' : 'Årets förlust'}</span>
                            <span className="ye-num">{formatSek(Math.abs(resultat))}</span>
                        </div>
                    </div>
                    <div className="ye-well" style={{ marginTop: '0.75rem' }}>
                        {vinst
                            ? 'Debet 8999 Årets resultat / Kredit 2019 Årets resultat, delägare 1.'
                            : 'Kredit 8999 Årets resultat / Debet 2019 Årets resultat, delägare 1.'}
                        {' '}I nästa års ingående balans förs beloppet vidare till ditt egna kapital.
                    </div>

                    {bookError && (
                        <div className="alert alert-danger mt-3 mb-0" style={{ fontSize: '0.875rem' }}>
                            {bookError}
                        </div>
                    )}

                    {!booked ? (
                        <div className="ye-proposal-actions">
                            <button
                                className="btn btn-primary d-inline-flex align-items-center gap-2"
                                onClick={handleBook}
                                disabled={booking}
                            >
                                <BookCheck size={16} />
                                {booking ? 'Bokför…' : 'Bokför årets resultat'}
                            </button>
                        </div>
                    ) : (
                        <>
                            <div className="ye-proposal-head" style={{ marginTop: '1rem' }}>
                                <span className="ye-badge ye-badge-success">
                                    <CheckCircle2 size={13} />
                                    Bokförd
                                </span>
                            </div>
                            {!confirmUndo ? (
                                <button
                                    className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center gap-2 mt-3"
                                    onClick={() => setConfirmUndo(true)}
                                    disabled={undoing}
                                >
                                    <Undo2 size={14} />
                                    Ångra bokföringen
                                </button>
                            ) : (
                                <div className="alert alert-danger mt-3 mb-0" style={{ fontSize: '0.875rem', maxWidth: 560 }}>
                                    Verifikatet nollställs med ett rättelseverifikat — inget raderas ur
                                    bokföringen. Vill du fortsätta?
                                    <div className="d-flex gap-2 mt-2">
                                        <button className="btn btn-danger btn-sm" onClick={handleUndoBooking} disabled={undoing}>
                                            {undoing ? 'Ångrar…' : 'Ja, ångra bokföringen'}
                                        </button>
                                        <button className="btn btn-outline-secondary btn-sm" onClick={() => setConfirmUndo(false)} disabled={undoing}>
                                            Avbryt
                                        </button>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}

            {gateError && (
                <div className="alert alert-danger d-flex align-items-center gap-2">
                    <AlertTriangle size={18} />
                    <span>{gateError.message}</span>
                </div>
            )}
            </div>

            <div className="ye-step-footer">
                {completed ? (
                    <>
                        <span className="ye-footer-done">
                            <CheckCircle2 size={18} style={{ color: 'var(--bs-success)' }} />
                            Klart — räkenskapsåret är låst
                        </span>
                        <button
                            className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center gap-2"
                            onClick={onUndo}
                            disabled={busy}
                        >
                            <Undo2 size={14} />
                            Ångra markeringen — öppna året igen
                        </button>
                    </>
                ) : (
                    <>
                        <span className="ye-footer-hint">
                            {booked
                                ? 'När du markerar steget som klart låses räkenskapsåret.'
                                : 'Bokför årets resultat först.'}
                        </span>
                        <button
                            className="btn btn-primary d-inline-flex align-items-center gap-2"
                            onClick={onComplete}
                            disabled={busy || !booked}
                        >
                            {busy ? 'Sparar…' : 'Markera som klart och lås året'}
                            <ArrowRight size={16} />
                        </button>
                    </>
                )}
            </div>
        </>
    );
};

export default EfResultStep;
